"use client";

import { forwardRef, type ChangeEvent, useCallback, useState } from "react";

import Input, { type InputProps } from "@/components/flowbite/forms/fields/input/input";
import { CancelSmallIcon, SearchIcon } from "@/components/icons-lib";
import { cn } from "@/shared/helpers/cn.helper";

export type SearchInputProps = Omit<InputProps, "icon" | "iconBefore" | "type"> & {
  /** When set, shows a trailing clear control when the field has a non-whitespace value. */
  onClear?: () => void;
  clearAriaLabel?: string;
};

const SearchInput = forwardRef<HTMLInputElement, SearchInputProps>((
  {
    inputClassName,
    onClear,
    clearAriaLabel = "Clear search",
    value,
    defaultValue,
    onChange,
    ...props
  },
  ref,
) => {
  const isControlled = value !== undefined;
  const [internalValue, setInternalValue] = useState(defaultValue?.toString() ?? "");
  const currentValueStr = String(isControlled ? (value ?? "") : internalValue);
  const showClear = Boolean(onClear && currentValueStr.trim() !== "");

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      if (!isControlled) {
        setInternalValue(event.target.value);
      }
      onChange?.(event);
    },
    [isControlled, onChange],
  );

  return (
    <Input
      {...props}
      ref={ref}
      defaultValue={defaultValue}
      icon={
        showClear ? (
          <button
            aria-label={clearAriaLabel}
            className="rounded p-0.5 hover:bg-gray-100"
            type="button"
            onClick={() => onClear?.()}
            onMouseDown={(e) => e.preventDefault()}
          >
            <CancelSmallIcon className="h-6 w-6 text-[#161515]" />
          </button>
        ) : undefined
      }
      iconBefore={<SearchIcon className="h-5 w-5" />}
      inputClassName={cn("placeholder:text-main-tertiary", inputClassName)}
      labelClassName="text-main-tertiary text-[10px] font-medium leading-[16px] tracking-[1px]"
      type="text"
      value={value}
      onChange={handleChange}
    />
  );
});

export default SearchInput;
